import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls } from "./form-controls";

interface Property {
  _id: string;
  slug: string;
  name: string;
  address: string;
  timezone: string;
}

// Landlord-only property settings: edit name, address, timezone.
// `slug` is the routing key — intentionally read-only here. Renaming
// the slug requires a coordinated host-registry + DNS change; do that
// out of band.
export function PropertySettingsDialog({
  open,
  onClose,
  property,
}: {
  open: boolean;
  onClose: () => void;
  property: Property;
}) {
  const update = useMutation(anyApi.properties.update);
  const [name, setName] = useState(property.name);
  const [address, setAddress] = useState(property.address);
  const [timezone, setTimezone] = useState(property.timezone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setName(property.name);
      setAddress(property.address);
      setTimezone(property.timezone);
      setError(null);
      setSavedAt(null);
    }
  }, [open, property._id, property.name, property.address, property.timezone]);

  const dirty =
    name !== property.name ||
    address !== property.address ||
    timezone !== property.timezone;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setBusy(true);
    setError(null);
    try {
      await update({
        propertyId: property._id,
        name: name !== property.name ? name : undefined,
        address: address !== property.address ? address : undefined,
        timezone: timezone !== property.timezone ? timezone : undefined,
      });
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Property settings" width="md">
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">
            Routing slug (read-only)
          </p>
          <p className="mt-1 font-mono text-sm text-slate-900">{property.slug}</p>
          <p className="mt-1 text-xs text-slate-500">
            Renaming the slug requires a coordinated host-registry + DNS
            update; do that out of band.
          </p>
        </div>

        <Field label="Name" required>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="Address" required>
          <input
            className={inputCls}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </Field>

        <Field label="Timezone" required>
          <input
            className={inputCls}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Los_Angeles"
          />
          <p className="mt-1 text-xs text-slate-500">
            IANA timezone. Affects how schedule times and rent periods
            are rendered.
          </p>
        </Field>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {savedAt && !dirty && !error && (
          <p className="text-xs text-emerald-700">Saved.</p>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={busy || !dirty}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
